import { LoadedSection, SupportedLocale } from '@msdk/definitions'

import { SectionByMicroservice } from '../../state/i18n'
import { ApiClient } from '../core'
import { ApiError } from '../core/errors'

export type I18nAPI = {
  getVersion(langToken: string): Promise<string>
  loadTranslations(section: SectionByMicroservice, langToken: string): Promise<LoadedSection>
}

export function initializeI18n(c: ApiClient): I18nAPI {
  /**
   * The function `getVersion` retrieves the translation version for a given language token.
   * @param {string} msName -  The `msName` parameter is a string that represents the name of the
   * microservice (MS) for which you want to load translations. It is used as a part of the API endpoint
   * to fetch the translations for the specified microservice.
   * @param {SupportedLocale} langToken - The `langToken` parameter is a token that represents the
   * supported locale or language. It is used to fetch the translation version for a specific language.
   * @returns The function `getVersion` returns a Promise that resolves to a string.
   */
  const getVersion = async (langToken: SupportedLocale): Promise<string> => {
    const res = await c.read<string>(`CMPS/api/Composer/TranslationVersion/${langToken}`)
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      // return res.slice(1, res.length - 1).slice(1, res.length - 2) as string
      return res
    }
  }

  /**
   * The function `loadTranslations` is an asynchronous function that loads translations for a given
   * microservice and language token.
   * @param {SectionByMicroservice} section - The `section` parameter is an object that contains the
   * name of the microservice and the name of the section for which you want to load translations.
   * @param {SupportedLocale} langToken - The `langToken` parameter is a string that represents the
   * language token or code for the desired translation. It is used to specify the language for which
   * translations should be loaded.
   * @returns The function `loadTranslations` returns a promise that resolves to a `LoadedSection`
   * object.
   */
  const loadTranslations = async (
    section: SectionByMicroservice,
    langToken: SupportedLocale,
  ): Promise<LoadedSection> => {
    const res = await c.read<LoadedSection>(`${section.msName}/api/Translation/${langToken}/${section.sectionName}`)
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      res.idmLs.forEach((x) => {
        x.tDescription = x.tDescription.replace(/\\n/g, '\n')
      })
      return res
    }
  }

  return { getVersion, loadTranslations }
}
